import { useInvoices } from "@/contexts/InvoicesContext";
import { styles } from "@/pages/InvoicesPage.styles";
import * as shared from "@/styles/shared";
import type { PaginationBarProps } from "@/types/ui";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import * as paginationBarStyles from "./PaginationBar.styles";

const PaginationBar = ({ page, totalPages, total, limit }: PaginationBarProps) => {
  const { setPage } = useInvoices();
  const { t } = useTranslation(["common", "components", "invoices"]);
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const pages: (number | "...")[] = [];
  const maxVisible = 5;
  const half = Math.floor(maxVisible / 2);
  let startPage = Math.max(1, page - half);
  const endPage = Math.min(totalPages, startPage + maxVisible - 1);
  if (endPage - startPage < maxVisible - 1) startPage = Math.max(1, endPage - maxVisible + 1);

  if (startPage > 1) {
    pages.push(1);
    if (startPage > 2) pages.push("...");
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) pages.push("...");
    pages.push(totalPages);
  }

  return (
    <div className={styles.pagination}>
      <p className={shared.muted}>{t("pagination.range", { ns: "invoices", start, end, total })}</p>
      <div className={styles.paginationButtons}>
        {/* First page */}
        <button
          disabled={page <= 1}
          onClick={() => setPage(1)}
          className={`${styles.paginationButton} ${page <= 1 ? paginationBarStyles.opacity40 : ""}`}
          title={t("components:firstPage")}
        >
          <ChevronsLeft className={shared.iconSm} />
        </button>

        {/* Previous */}
        <button
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
          className={`${styles.paginationButton} ${page <= 1 ? paginationBarStyles.opacity40 : ""}`}
          title={t("components:previousPage")}
        >
          <ChevronLeft className={shared.iconSm} />
        </button>

        {/* Page numbers */}
        {pages.map((p, idx) =>
          p === "..." ? (
            <span key={`ellipsis-${idx}`} className={paginationBarStyles.paginationEllipsis}>
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => setPage(p as number)}
              className={`${styles.paginationButton} ${p === page ? styles.paginationButtonActive : ""}`}
            >
              {p}
            </button>
          ),
        )}

        {/* Next */}
        <button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
          className={`${styles.paginationButton} ${page >= totalPages ? paginationBarStyles.opacity40 : ""}`}
          title={t("components:nextPage")}
        >
          <ChevronRight className={shared.iconSm} />
        </button>

        {/* Last page */}
        <button
          disabled={page >= totalPages}
          onClick={() => setPage(totalPages)}
          className={`${styles.paginationButton} ${page >= totalPages ? paginationBarStyles.opacity40 : ""}`}
          title={t("components:lastPage")}
        >
          <ChevronsRight className={shared.iconSm} />
        </button>
      </div>
    </div>
  );
};

export { PaginationBar };
